import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateRevisionParams {
  leadId: string;
  changeSummary?: string;
}

export const useCreateQuoteRevision = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, changeSummary }: CreateRevisionParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get the latest quotation for this lead
      const { data: quotation, error: quotationError } = await supabase
        .from("quotations")
        .select("*")
        .eq("lead_id", leadId)
        .is("archived_at", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (quotationError) throw quotationError;
      if (!quotation) throw new Error("No quotation found for this lead");

      // Get current itinerary items for snapshot
      const { data: itineraryItems, error: itemsError } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: true });

      if (itemsError) throw itemsError;

      // Get the latest revision number
      const { data: latestRevision, error: revisionCheckError } = await supabase
        .from("quote_revisions")
        .select("revision_number")
        .eq("quotation_id", quotation.id)
        .order("revision_number", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (revisionCheckError) throw revisionCheckError;

      const nextRevisionNumber = latestRevision ? latestRevision.revision_number + 1 : 1;

      // Create new revision snapshot
      const { error: revisionError } = await supabase
        .from("quote_revisions")
        .insert({
          quotation_id: quotation.id,
          lead_id: leadId,
          user_id: user.id,
          revision_number: nextRevisionNumber,
          quote_number: quotation.quote_number,
          subtotal: quotation.subtotal,
          markup_percentage: quotation.markup_percentage,
          markup_amount: quotation.markup_amount,
          total_amount: quotation.total_amount,
          valid_until: quotation.valid_until,
          terms_and_conditions: quotation.terms_and_conditions,
          notes: quotation.notes,
          status: "accepted",
          itinerary_snapshot: itineraryItems || [],
          change_summary: changeSummary || `Quote revision ${nextRevisionNumber}`,
        });

      if (revisionError) throw revisionError;

      // Log the activity
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Quote Revised",
        details: `Quote revision ${nextRevisionNumber} created: ${changeSummary || "Itinerary updated"}`,
      });

      return { revisionNumber: nextRevisionNumber };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["quote-revisions"] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", variables.leadId] });
      toast.success(`Quote revision ${data.revisionNumber} created successfully!`);
    },
    onError: (error: any) => {
      toast.error(`Failed to create revision: ${error.message}`);
    },
  });
};
